import { useCallback, useEffect, useState } from "react";
import { toast } from "@/components/ui/sonner";
import api, { apiError, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel } from "@/components/terminal";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Users, Globe } from "lucide-react";

const INPUT = "bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-2 py-1.5 text-xs text-slate-200 w-full font-mono";
const LOCK = "px-1.5 py-0.5 rounded-sm border text-[9px] font-bold tracking-widest bg-slate-500/10 text-slate-300 border-slate-500/30";

export default function EnterpriseDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "enterprise_admin";
  const isAuditor = user?.role === "auditor";
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [doctrine, setDoctrine] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pf, setPf] = useState({});
  const [if_, setIf] = useState({ email: "", role: "auditor" });
  const [sIdent, setSIdent] = useState(null);
  const [sInvoices, setSInvoices] = useState([]);
  const [payTx, setPayTx] = useState({});

  const load = useCallback(() => {
    api.get("/enterprise/tenant").then((r) => { setTenant(r.data); setPf({
      display_name: r.data.display_name || "", primary_contact_email: r.data.primary_contact_email || "",
      notes: r.data.notes || "" }); }).catch((e) => toast.error(apiError(e)));
    api.get("/enterprise/tenant/users").then((r) => setUsers(r.data.users)).catch(() => {});
    api.get("/enterprise/tenant/doctrine").then((r) => setDoctrine(r.data)).catch(() => {});
    api.get("/enterprise/tenant/invitations").then((r) => setInvites(r.data.invitations)).catch(() => {});
    api.get("/enterprise/settlement/identity").then((r) => setSIdent(r.data)).catch(() => {});
    api.get("/enterprise/settlement/invoices").then((r) => setSInvoices(r.data.invoices)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveProfile = () => {
    setBusy("profile");
    api.patch("/enterprise/tenant", pf)
      .then(() => { toast.success("Tenant profile updated — audit-chained"); load(); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  const invite = () => {
    setBusy("invite");
    api.post("/enterprise/tenant/invitations", if_)
      .then((r) => { toast.success("Invitation issued — deliver privately"); setIf({ email: "", role: "auditor" });
                     window.prompt("Private invitation link (deliver out-of-band):",
                                   `${window.location.origin}${r.data.invite_path}`); load(); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  const selectAsset = (id, asset) => {
    setBusy(`sel-${id}`);
    api.post(`/enterprise/settlement/invoices/${id}/select-asset`, { asset })
      .then(() => { toast.success(`${asset} selected — rate snapshot recorded`); load(); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  const submitPayment = (id) => {
    setBusy(`pay-${id}`);
    api.post(`/enterprise/settlement/invoices/${id}/submit-payment`, { tx_hash: payTx[id] })
      .then((r) => {
        if (r.data.status === "PAID") toast.success("Payment reconciled — invoice PAID");
        else toast.success("Payment detected on-chain — confirming");
        setPayTx({ ...payTx, [id]: "" }); load();
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  const refreshPayment = (id) => {
    setBusy(`ref-${id}`);
    api.post(`/enterprise/settlement/invoices/${id}/refresh-payment`, {})
      .then(() => load())
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setBusy(null));
  };

  return (
    <div className="space-y-6" data-testid="enterprise-dashboard">
      <div>
        <h1 className="text-2xl font-bold font-display" data-testid="enterprise-title">
          {tenant?.display_name || tenant?.org_name || "Enterprise Workspace"}
        </h1>
        <p className="text-xs text-slate-500 font-mono mt-1" data-testid="enterprise-positioning">
          {doctrine?.positioning || "Hussar verifies ownership, provenance, controls, and settlement. Hussar does not take third-party custody."}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="enterprise-doctrine-locks">
          <span className={LOCK}>CUSTOMER CUSTODY: {doctrine?.customer_custody || "DISABLED"}</span>
          <span className={LOCK}>SETTLEMENT: {doctrine?.settlement_status || "NOT CONFIGURED"}</span>
          <span className={LOCK}>ROLE: {(user?.role || "").replace(/_/g, " ").toUpperCase()}</span>
          {isAuditor && <span className={`${LOCK} border-amber-500/30 text-amber-300 bg-amber-500/10`} data-testid="auditor-readonly-badge">READ-ONLY AUDITOR</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Organization Profile" testId="ent-profile-panel">
          {tenant ? (
            <div className="space-y-2">
              <div className="text-[10px] font-mono text-slate-500">TENANT <span className="text-[#D4AF37]">{tenant.tenant_id}</span> · STATUS {tenant.status}</div>
              {isAdmin ? (
                <>
                  <input data-testid="ent-display-name" className={INPUT} placeholder="Display name" value={pf.display_name}
                         onChange={(e) => setPf({ ...pf, display_name: e.target.value })} />
                  <input data-testid="ent-contact-email" className={INPUT} placeholder="Primary contact email" value={pf.primary_contact_email}
                         onChange={(e) => setPf({ ...pf, primary_contact_email: e.target.value })} />
                  <textarea data-testid="ent-notes" rows={2} className={INPUT} placeholder="Notes" value={pf.notes}
                            onChange={(e) => setPf({ ...pf, notes: e.target.value })} />
                  <Button data-testid="ent-profile-save" disabled={busy === "profile"} onClick={saveProfile}
                          className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs w-full">
                    {busy === "profile" ? "SAVING…" : "SAVE PROFILE"}
                  </Button>
                </>
              ) : (
                <div className="text-xs font-mono text-slate-400 space-y-1" data-testid="ent-profile-readonly">
                  <div>Contact: {tenant.primary_contact_email || "—"}</div>
                  <div>Created: {fmtTime(tenant.created_at)} · Approved by platform review</div>
                  {tenant.notes && <div>Notes: {tenant.notes}</div>}
                  <div className="text-slate-600">Read-only — profile changes require the enterprise_admin role.</div>
                </div>
              )}
            </div>
          ) : <div className="text-xs text-slate-600 font-mono py-4">LOADING…</div>}
        </Panel>

        <Panel title={`Users (${users.length})`} testId="ent-users-panel">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
              <th className="py-2 px-2">User</th><th className="py-2 px-2">Role</th><th className="py-2 px-2">Joined</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#1E2128]" data-testid={`ent-user-${u.id}`}>
                  <td className="py-2 px-2 font-mono text-slate-200">{u.name} <span className="text-slate-500">{u.email}</span></td>
                  <td className="py-2 px-2"><span className={LOCK}>{u.role.replace(/_/g, " ").toUpperCase()}</span></td>
                  <td className="py-2 px-2 font-mono text-slate-500">{fmtTime(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {isAdmin && (
            <div className="mt-4 pt-3 border-t border-[#222630] space-y-2" data-testid="ent-invite-panel">
              <div className="text-[10px] font-mono tracking-widest text-slate-500 uppercase">Invite member or auditor (one-time, 72h, email-bound)</div>
              <div className="grid grid-cols-3 gap-2">
                <input data-testid="ent-invite-email" className={`${INPUT} col-span-2`} placeholder="Email" value={if_.email}
                       onChange={(e) => setIf({ ...if_, email: e.target.value })} />
                <select data-testid="ent-invite-role" className={INPUT} value={if_.role}
                        onChange={(e) => setIf({ ...if_, role: e.target.value })}>
                  <option value="auditor">auditor (read-only)</option>
                  <option value="enterprise_member">enterprise_member</option>
                </select>
              </div>
              <Button data-testid="ent-invite-btn" disabled={busy === "invite" || !if_.email} onClick={invite}
                      className="bg-[#D4AF37] text-black hover:bg-[#C5A059] font-mono text-xs w-full">
                {busy === "invite" ? "ISSUING…" : "ISSUE PRIVATE INVITATION"}
              </Button>
              {invites.length > 0 && (
                <div className="space-y-1 mt-2" data-testid="ent-invites-list">
                  {invites.map((i) => (
                    <div key={i.invitation_id} className="text-[10px] font-mono text-slate-500" data-testid={`ent-invite-${i.invitation_id}`}>
                      {i.email} · {i.role} · {i.consumed_at ? "CONSUMED" : i.revoked_at ? "REVOKED" : "OPEN"} · exp {fmtTime(i.expires_at)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Settlement Identity" testId="ent-settlement-identity">
        {sIdent ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <span className="font-mono text-sm text-[#D4AF37]" data-testid="settlement-identity-name">{sIdent.identity?.name || "—"}</span>
                <span className="ml-2 text-[10px] font-mono text-slate-500">{sIdent.identity?.registrar} · {sIdent.identity?.legal_owner}</span>
              </div>
              <span className={LOCK}>{(sIdent.identity?.status || "").toUpperCase()}</span>
            </div>
            <p className="text-[11px] font-mono text-slate-400 leading-relaxed" data-testid="settlement-doctrine">{sIdent.doctrine}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="settlement-locks">
              <span className={LOCK}>SOLICITATION: {sIdent.identity?.public_solicitation}</span>
              <span className={LOCK}>CUSTOMER CUSTODY: {sIdent.identity?.customer_custody}</span>
              <span className={LOCK}>AUTO-PAYMENT: {sIdent.identity?.auto_payment_access}</span>
            </div>
            <ol className="grid grid-cols-1 md:grid-cols-4 gap-1.5" data-testid="settlement-flow">
              {(sIdent.flow || []).map((s, i) => (
                <li key={s} className="text-[10px] font-mono text-slate-400 border border-[#222630] rounded-sm px-2 py-1.5">
                  <span className="text-[#D4AF37]">{i + 1}.</span> {s}
                </li>
              ))}
            </ol>
            <div className="space-y-1" data-testid="settlement-active-mappings">
              {(sIdent.active_mappings || []).length === 0 && (
                <div className="text-[10px] font-mono text-slate-600">No active settlement address mappings yet — addresses activate only after dual control + verification.</div>
              )}
              {(sIdent.active_mappings || []).map((m) => (
                <div key={m.network} className="text-[10px] font-mono text-slate-300" data-testid={`active-mapping-${m.network}`}>
                  {m.network}: <span className="text-[#D4AF37]">{m.address}</span> · verified {fmtTime(m.verified_at)}
                </div>
              ))}
            </div>
          </div>
        ) : <div className="text-xs text-slate-600 font-mono py-4">LOADING…</div>}
      </Panel>

      <Panel title={`Invoices (${sInvoices.length})`} testId="ent-invoices-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-[#222630]">
              <th className="py-2 px-2">Invoice</th><th className="py-2 px-2 text-right">USD</th>
              <th className="py-2 px-2">Asset</th><th className="py-2 px-2 text-right">Expected</th>
              <th className="py-2 px-2 text-right">Paid</th><th className="py-2 px-2">Status</th>
              <th className="py-2 px-2">Pay to / tx</th></tr></thead>
            <tbody>
              {sInvoices.map((i) => (
                <tr key={i.invoice_id} className="border-b border-[#1E2128]" data-testid={`inv-row-${i.invoice_id}`}>
                  <td className="py-2 px-2 font-mono text-[#D4AF37]">{i.invoice_id}
                    <span className="block text-[9px] text-slate-500">{i.description} · due {fmtTime(i.due_at)}</span></td>
                  <td className="py-2 px-2 font-mono text-slate-200 text-right">${i.amount_usd}</td>
                  <td className="py-2 px-2 font-mono text-slate-400">
                    {i.asset || (isAdmin && i.status === "ISSUED" ? (
                      <span className="space-x-1">
                        <button data-testid={`inv-select-btc-${i.invoice_id}`} disabled={busy === `sel-${i.invoice_id}`}
                                onClick={() => selectAsset(i.invoice_id, "BTC")}
                                className="px-1.5 py-0.5 border border-[#2D323E] rounded-sm text-[9px] font-bold text-slate-300 hover:text-[#D4AF37]">BTC</button>
                        <button data-testid={`inv-select-xrp-${i.invoice_id}`} disabled={busy === `sel-${i.invoice_id}`}
                                onClick={() => selectAsset(i.invoice_id, "XRP")}
                                className="px-1.5 py-0.5 border border-[#2D323E] rounded-sm text-[9px] font-bold text-slate-300 hover:text-[#D4AF37]">XRP</button>
                      </span>
                    ) : "—")}
                  </td>
                  <td className="py-2 px-2 font-mono text-slate-200 text-right">{i.expected_crypto ?? "—"}</td>
                  <td className="py-2 px-2 font-mono text-slate-200 text-right">{i.paid_total ?? 0}</td>
                  <td className="py-2 px-2"><span className={LOCK}>{i.status.replace(/_/g, " ")}</span></td>
                  <td className="py-2 px-2 font-mono text-[10px] text-slate-400">
                    {i.status === "AWAITING_PAYMENT" && (
                      <div className="space-y-1">
                        <div data-testid={`inv-address-${i.invoice_id}`}>Send exactly {i.expected_crypto} {i.asset} to <span className="text-[#D4AF37]">{i.settlement_address}</span></div>
                        {(i.payments || []).map((p) => (
                          <div key={p.tx_hash} className="text-slate-500" data-testid={`inv-payment-${p.tx_hash}`}>
                            {p.observed} {i.asset} · {p.confirmations}/{p.threshold} conf · {p.status}
                          </div>
                        ))}
                        {isAdmin && (
                          <div className="flex gap-1">
                            <input data-testid={`inv-tx-${i.invoice_id}`} value={payTx[i.invoice_id] || ""} placeholder="tx hash"
                                   onChange={(e) => setPayTx({ ...payTx, [i.invoice_id]: e.target.value.trim() })}
                                   className="bg-[#0B0C0E] border border-[#2D323E] rounded-sm px-1.5 py-0.5 text-[10px] text-slate-200 font-mono w-40" />
                            <button data-testid={`inv-submit-${i.invoice_id}`} disabled={busy === `pay-${i.invoice_id}` || !payTx[i.invoice_id]}
                                    onClick={() => submitPayment(i.invoice_id)}
                                    className="px-1.5 py-0.5 border border-[#D4AF37]/50 rounded-sm text-[9px] font-bold text-[#D4AF37]">SUBMIT</button>
                          </div>
                        )}
                        <button data-testid={`inv-refresh-${i.invoice_id}`} disabled={busy === `ref-${i.invoice_id}`}
                                onClick={() => refreshPayment(i.invoice_id)}
                                className="px-1.5 py-0.5 border border-[#2D323E] rounded-sm text-[9px] font-bold text-slate-400">REFRESH CONFIRMATIONS</button>
                      </div>
                    )}
                    {i.status === "PAID" && <span className="text-emerald-400" data-testid={`inv-paid-${i.invoice_id}`}>Reconciled {fmtTime(i.paid_at)} — payment never creates access.</span>}
                    {i.status === "VOID" && <span className="text-red-400">Voided{i.void?.reason ? `: ${i.void.reason}` : ""}</span>}
                  </td>
                </tr>
              ))}
              {sInvoices.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-600 font-mono text-xs" data-testid="ent-invoices-empty">No invoices.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Access & Assurance Doctrine" testId="ent-doctrine-panel">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[11px] font-mono text-slate-400 leading-relaxed">
          <div>
            <div className="text-slate-200 text-xs mb-1 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" /> Access model</div>
            {doctrine?.access_model || "Request Access → Internal Review → Approval → Private Invitation → Enterprise Onboarding"}
          </div>
          <div>
            <div className="text-slate-200 text-xs mb-1 flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-[#D4AF37]" /> Isolation</div>
            Your organization's data is tenant-scoped server-side on every query. Cross-tenant aggregation exists only under platform-owner authority, audit-logged.
          </div>
        </div>
      </Panel>
    </div>
  );
}
